import React, {useEffect, useState, useRef} from 'react';
import axios from 'axios';
import {useHistory, useParams} from "react-router-dom";
import {Button} from 'primereact/button';
import {DataTable} from 'primereact/datatable';
import {Column} from 'primereact/column';
import {Dialog} from 'primereact/dialog';
import {ProgressBar} from 'primereact/progressbar';
import {Dropdown} from "primereact/dropdown";

import LocationGeometryFormComponent from "./LocationGeometryFormComponent";

const LocationGeometryListComponent = ({props, locationId, canWrite, t}) => {
  const [locationGeometries, setLocationGeometries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [rows, setRows] = useState(Number(process.env.REACT_APP_LIST_ROW_DEFAULT));
  const [dialogVisible, setDialogVisible] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  const dt = useRef(null);
  const loadLazyTimeout = useRef(null);

  const [lazyParams, setLazyParams] = useState({
    first: 0,
    rows: Number(process.env.REACT_APP_LIST_ROW_DEFAULT),
    page: 0,
    sortField: 'id',
    sortOrder: -1,
  });

  const apiCall = (method, url, data) => {
    return axios({
      method,
      url: process.env.REACT_APP_API_HOST + url,
      data,
      params: {},
      headers: {
        'Authorization': 'Bearer ' + localStorage.getItem("userToken"),
        'App-Network': localStorage.getItem("networkId")
      }
    });
  };

  const loadLazyData = () => {
    if (!locationId) {
      return;
    }

    setLoading(true);

    if (loadLazyTimeout.current) {
      clearTimeout(loadLazyTimeout.current);
    }

    loadLazyTimeout.current = setTimeout(() => {
      let query = window.App.transformQueryParams(lazyParams);

      const separator = query.includes('?') ? '&' : '?';

      query += separator +
        'search[location][operator]==' +
        '&search[location][value]=' + locationId;

      apiCall('get', '/api/v2/location-geometry' + query)
        .then(response => {
          setTotalRecords(response.data.data.total_item_count);
          setLocationGeometries(response.data.data.items || []);
        })
        .catch(error => {
          window.App.toastShow('error', t('error'), error.toString(), '', error.response);
        })
        .finally(() => {
          setLoading(false);
        });
    }, Math.random() * 1000 + 250);
  };

  const onPage = (event) => {
    let _lazyParams = {...lazyParams, ...event};
    setLazyParams(_lazyParams);
  };

  const onSort = (event) => {
    let _lazyParams = {...lazyParams, ...event};
    setLazyParams(_lazyParams);
  };

  const onFilter = (event) => {
    let _lazyParams = {...lazyParams, ...event};
    _lazyParams['first'] = 0;
    _lazyParams['page'] = 0;
    setLazyParams(_lazyParams);
  };

  const pager = {
    layout: 'RowsPerPageDropdown CurrentPageReport PrevPageLink NextPageLink',
    'RowsPerPageDropdown': (options) => {
      const dropdownOptions = [
        {label: Number(process.env.REACT_APP_LIST_ROW_OP1), value: Number(process.env.REACT_APP_LIST_ROW_OP1)},
        {label: Number(process.env.REACT_APP_LIST_ROW_OP2), value: Number(process.env.REACT_APP_LIST_ROW_OP2)},
        {label: Number(process.env.REACT_APP_LIST_ROW_OP3), value: Number(process.env.REACT_APP_LIST_ROW_OP3)},
        {label: Number(process.env.REACT_APP_LIST_ROW_OP4), value: Number(process.env.REACT_APP_LIST_ROW_OP4)},
      ];

      return (
        <>
          <Dropdown
            value={options.value}
            options={dropdownOptions}
            onChange={(e) => {
              setRows(e.target.value);
              setLazyParams({
                ...lazyParams,
                rows: e.target.value,
                first: 0,
                page: 0
              });
            }}
            appendTo={document.body}
          />
        </>
      );
    },
    'CurrentPageReport': (options) => {
      return (
        <span style={{color: 'var(--text-color)', userSelect: 'none', width: '120px', textAlign: 'center'}}>
          {options.first} - {options.last} / {options.totalRecords}
        </span>
      );
    }
  };

  const openCreate = () => {
    setEditingItem(null);
    setDialogVisible(true);
  };

  const openEdit = (rowData) => {
    setEditingItem(rowData);
    setDialogVisible(true);
  };

  const closeDialog = () => {
    setDialogVisible(false);
    setEditingItem(null);
  };

  const onFormSuccess = () => {
    closeDialog();
    window.App.setState({rerenderLocationGeometryList: Math.random()});
  };

  const deleteItem = (rowData) => {
    if (window.confirm('A törlés megerősítésére van szükség!')) {
      setLoading(true);

      apiCall('delete', '/api/v2/location-geometry/' + rowData.id)
        .then(() => {
          window.App.toastShow('success', 'Sikeres törlés!');
          window.App.setState({rerenderLocationGeometryList: Math.random()});
        })
        .catch(error => {
          window.App.toastShow('error', t('error'), error.toString(), '', error.response);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  const actionBodyTemplate = (rowData) => {
    return (
      <React.Fragment>
        {localStorage.getItem('roles').toString().includes('role_location_geometry_write') &&
          <Button
            icon="pi pi-pencil"
            className="p-button-warning p-mr-2"
            tooltip="Módosítás"
            tooltipOptions={{position: 'left'}}
            onClick={() => openEdit(rowData)}
          />
        }

        {localStorage.getItem('roles').toString().includes('role_location_geometry_write') &&
          <Button
            icon="pi pi-trash"
            className="p-button-danger"
            tooltip="Törlés"
            tooltipOptions={{position: 'left'}}
            onClick={() => deleteItem(rowData)}
          />
        }
      </React.Fragment>
    );
  };

  useEffect(() => {
    loadLazyData();
  }, [lazyParams, locationId, window.App.state.rerenderLocationGeometryList]);

  return (
    <div className="location-geometry-list widget p-mt-4">
      {loading &&
        <ProgressBar
          mode="indeterminate"
          style={{height: '5px', borderRadius: 0}}
        />
      }

      <div className="p-grid p-mb-3">
        <div className="p-col-12 p-lg-6 p-col-align-center p-text-left">
          <h3 className="p-m-0">{t('locationGeometry')}</h3>
        </div>

        <div className="p-col-12 p-lg-6 p-col-align-center p-text-right">
          {localStorage.getItem('roles').toString().includes('role_location_geometry_write') &&
            <Button
              label={t('addLocationGeometry')}
              icon="pi pi-plus"
              className="p-button-success"
              onClick={openCreate}
            />
          }
        </div>
      </div>

      <DataTable
        size={localStorage.getItem('scale')}
        emptyMessage={t('noResults')}
        value={locationGeometries}
        paginator
        first={lazyParams.first}
        totalRecords={totalRecords}
        onPage={onPage}
        onSort={onSort}
        sortField={lazyParams.sortField}
        sortOrder={lazyParams.sortOrder}
        onFilter={onFilter}
        filters={lazyParams.filters}
        loading={loading}
        lazy
        paginatorTemplate={pager}
        rows={rows}
        autoLayout={true}
        filterDisplay="row"
        responsiveLayout="scroll"
        stripedRows
        ref={dt}
        dataKey="id"
      >
        <Column field="name" header="Megnevezés" sortable filter />
        <Column body={actionBodyTemplate} className={"p-py-0 p-text-right p-text-nowrap"}/>
      </DataTable>

      <Dialog
        header={editingItem ? t('locationGeometryUpdate') : t('addLocationGeometry')}
        visible={dialogVisible}
        style={{width: '80vw'}}
        modal
        onHide={closeDialog}
      >
        <LocationGeometryFormComponent
          locationId={locationId}
          item={editingItem}
          t={t}
          onCancel={closeDialog}
          onSuccess={onFormSuccess}
        />
      </Dialog>
    </div>
  );
};

export default LocationGeometryListComponent;